/* iCrunch theme — v3
   Modules: utils → theme colours → sound → chapters (video deck) → hero → passport → analytics
   No dependencies. Everything is defensive: a missing section just skips its module. */
(() => {
  "use strict";

  const doc = document;
  const root = doc.documentElement;
  const body = doc.body;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));
  const pad2 = (n) => String(n).padStart(2, "0");

  const store = {
    get(key, fallback) {
      try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch (e) { return fallback; }
    },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ } }
  };

  const track = (name, params) => {
    const data = Object.assign({}, params || {});
    try {
      if (typeof window.gtag === "function") window.gtag("event", name, data);
      (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data));
    } catch (e) { /* analytics never breaks the page */ }
  };

  // iOS ignores video.volume (always 1). Detect once so we don't attempt fades there.
  const canSetVolume = (() => {
    try { const v = doc.createElement("video"); v.volume = 0.5; return v.volume === 0.5; } catch (e) { return false; }
  })();

  /* ------------------------------------------------------------------ theme colours */
  const theme = {
    set(accent, glow, key) {
      if (accent) root.style.setProperty("--accent", accent);
      if (glow) root.style.setProperty("--glow", glow);
      if (key) body.dataset.flavour = key;
    }
  };

  /* ------------------------------------------------------------------ sound manager */
  const sound = (() => {
    const pref = store.get("icrunch:sound", null); // null = never chosen, true/false = chosen
    let on = pref === true;
    let unlocked = false; // becomes true after the first real user gesture
    const listeners = new Set();

    const toggles = () => [...doc.querySelectorAll("[data-sound-toggle]")];

    function render() {
      toggles().forEach((btn) => {
        btn.setAttribute("aria-pressed", on ? "true" : "false");
        const label = btn.querySelector("[data-sound-label]");
        if (label) {
          if (!label.dataset.off) label.dataset.off = label.textContent.trim() || "Sound off";
          label.textContent = on ? "Sound on" : label.dataset.off;
        }
      });
    }

    function emit() { listeners.forEach((fn) => { try { fn(on, unlocked); } catch (e) { /* noop */ } }); }

    function set(value, source) {
      on = !!value;
      store.set("icrunch:sound", on);
      render();
      emit();
      track(on ? "icrunch_sound_on" : "icrunch_sound_off", { source: source || "toggle" });
    }

    function unlock() {
      if (unlocked) return;
      unlocked = true;
      emit();
    }

    // Any real gesture counts as an unlock (needed to honour a remembered "sound on").
    ["pointerdown", "keydown", "touchend"].forEach((evt) => {
      doc.addEventListener(evt, unlock, { capture: true, passive: true });
    });

    doc.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-sound-toggle]");
      if (!btn) return;
      unlocked = true;
      set(!on, "toggle");
    });

    render();

    return {
      get on() { return on; },
      get unlocked() { return unlocked; },
      get explicitOff() { return store.get("icrunch:sound", null) === false; },
      set,
      unlock,
      markBlocked() { unlocked = false; render(); emit(); },
      subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
    };
  })();

  /* ------------------------------------------------------------------ chapters (video deck) */
  let chaptersApi = null;
  let passportApi = null;

  function initChapters(section) {
    const stage = section.querySelector(".chapters-stage");
    const trackEl = section.querySelector(".chapters-track");
    const chapters = [...section.querySelectorAll(".chapter")];
    if (!stage || !trackEl || !chapters.length) return null;

    const videos = chapters.map((ch) => ch.querySelector(".chapter-video"));
    const navButtons = [...section.querySelectorAll(".chapter-nav [data-goto]")];
    const nextBtn = section.querySelector(".chapter-next");
    const counterEl = section.querySelector("[data-counter-current]");
    const gate = section.querySelector("[data-sound-gate]");
    const volume = clamp((parseInt(section.dataset.volume, 10) || 80) / 100, 0, 1);
    const count = chapters.length;
    const keys = chapters.map((c) => c.dataset.chapter);

    let active = 0;
    let stepPx = 0;
    let sectionTop = 0;
    let inView = false;
    let raf = 0;
    let leaveTimer = 0;
    let stampTimer = 0;
    let gateDismissed = false;

    if (section.dataset.snap === "true" && !reduceMotion) root.classList.add("has-snap");

    function measure() {
      stepPx = stage.offsetHeight || window.innerHeight;
      sectionTop = section.getBoundingClientRect().top + window.scrollY;
    }

    /* ---- video sources: Safari gets HLS (adaptive), everyone else gets the best MP4 for their screen */
    function chooseSrc(v) {
      const hls = v.dataset.hls || "";
      const hd = v.dataset.hd || "";
      const sd = v.dataset.sd || "";
      const conn = navigator.connection || {};
      const frugal = !!conn.saveData || /(^|-)2g$/.test(conn.effectiveType || "");
      const small = window.innerWidth < 760;
      if (hls && v.canPlayType("application/vnd.apple.mpegurl")) return hls;
      if ((frugal || small) && sd) return sd;
      return hd || sd || "";
    }

    function load(i, eager) {
      const v = videos[i];
      if (!v || reduceMotion) return;
      if (!v.dataset.loaded) {
        const src = chooseSrc(v);
        if (!src) return;
        v.dataset.loaded = "1";
        v.addEventListener("playing", () => chapters[i].classList.add("video-ready"), { once: true });
        v.addEventListener("error", () => chapters[i].classList.remove("video-ready"), { once: true });
        v.src = src;
      }
      if (eager) v.preload = "auto";
    }

    function fadeVolume(v, to, ms, done) {
      if (!canSetVolume || ms <= 0) { v.volume = canSetVolume ? to : 1; if (done) done(); return; }
      cancelAnimationFrame(v._fade || 0);
      const from = v.volume;
      const start = performance.now();
      const step = (t) => {
        const p = clamp((t - start) / ms, 0, 1);
        v.volume = from + (to - from) * p;
        if (p < 1) v._fade = requestAnimationFrame(step);
        else if (done) done();
      };
      v._fade = requestAnimationFrame(step);
    }

    function playVideo(i) {
      const v = videos[i];
      if (!v || reduceMotion) return;
      load(i, true);
      const withSound = sound.on && sound.unlocked;
      if (withSound) {
        v.muted = false;
        if (canSetVolume) v.volume = 0;
      } else {
        v.muted = true;
      }
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Sound was refused (no gesture yet) — fall back to a muted loop and ask again.
          if (!v.muted) {
            v.muted = true;
            v.play().catch(() => {});
            sound.markBlocked();
          }
        });
      }
      if (withSound) fadeVolume(v, volume, 600);
    }

    function stopVideo(i, immediate) {
      const v = videos[i];
      if (!v) return;
      cancelAnimationFrame(v._fade || 0);
      const finish = () => { v.pause(); v.muted = true; };
      if (immediate || v.muted || !canSetVolume) finish();
      else fadeVolume(v, 0, 320, finish);
    }

    function applySound() {
      const v = videos[active];
      renderGate();
      if (!v || reduceMotion || !inView) return;
      if (sound.on && sound.unlocked) {
        if (v.paused) { playVideo(active); return; }
        v.muted = false;
        if (canSetVolume) { v.volume = 0; fadeVolume(v, volume, 400); }
      } else {
        v.muted = true;
      }
    }

    /* ---- sound gate: the big "tap for sound" prompt */
    function renderGate() {
      if (!gate) return;
      const show = !gateDismissed && !sound.explicitOff && !(sound.on && sound.unlocked) && active < 2;
      if (show) { gate.hidden = false; gate.classList.remove("is-hiding"); }
      else if (!gate.hidden) {
        gate.classList.add("is-hiding");
        setTimeout(() => { gate.hidden = true; }, 320);
      }
    }
    if (gate) {
      gate.addEventListener("click", () => {
        gateDismissed = true;
        sound.unlock();
        sound.set(true, "gate");
      });
    }

    /* ---- passport stamp after a short dwell on a chapter */
    function scheduleStamp(key) {
      clearTimeout(stampTimer);
      stampTimer = setTimeout(() => {
        if (inView && keys[active] === key && passportApi) passportApi.stamp(key);
      }, 1400);
    }

    /* ---- switching slides */
    function activate(i, dir, instant) {
      if (i === active) return;
      const prev = active;
      active = i;
      const from = chapters[prev];
      const to = chapters[i];

      chapters.forEach((c) => { if (c !== from) c.classList.remove("is-leaving"); });
      to.style.setProperty("--wipe-from", dir < 0 ? "0 0 100% 0" : "100% 0 0 0");
      if (instant) to.style.transition = "none";
      void to.offsetWidth; // apply the new start edge before transitioning
      from.classList.remove("is-active");
      from.classList.add("is-leaving");
      to.classList.add("is-active");
      if (instant) { requestAnimationFrame(() => { to.style.transition = ""; }); }

      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(() => from.classList.remove("is-leaving"), instant ? 0 : 900);

      stopVideo(prev, instant);
      if (inView) playVideo(i);
      if (i + 1 < count) load(i + 1, false);

      theme.set(to.dataset.accent, to.dataset.glow, to.dataset.chapter);
      navButtons.forEach((b, k) => { if (k === i) b.setAttribute("aria-current", "true"); else b.removeAttribute("aria-current"); });
      if (counterEl) counterEl.textContent = pad2(i + 1);
      section.classList.toggle("is-last", i === count - 1);
      renderGate();
      scheduleStamp(to.dataset.chapter);
      track("icrunch_chapter_view", { chapter: to.dataset.chapter, index: i });
      doc.dispatchEvent(new CustomEvent("icrunch:chapter", { detail: { key: to.dataset.chapter, index: i } }));
    }

    /* ---- scroll → progress → active slide + parallax */
    function update() {
      raf = 0;
      if (!stepPx) measure();
      const top = trackEl.getBoundingClientRect().top;
      const progress = clamp(-top / stepPx, 0, count);
      const idx = clamp(Math.round(progress), 0, count - 1);
      if (idx !== active) activate(idx, idx > active ? 1 : -1, false);
      const sub = clamp(progress - idx, -0.5, 0.5);
      chapters[active].style.setProperty("--sub", sub.toFixed(3));
    }
    function onScroll() { if (!raf) raf = requestAnimationFrame(update); }

    let resizeTimer = 0;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { measure(); update(); }, 120);
    }

    function goTo(i) {
      i = clamp(i, 0, count - 1);
      measure();
      window.scrollTo({ top: Math.round(sectionTop + i * stepPx), behavior: reduceMotion ? "auto" : "smooth" });
    }
    function goToKey(key) {
      const i = keys.indexOf(key);
      if (i >= 0) goTo(i); else section.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
    }

    section.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-goto]");
      if (!btn) return;
      const target = btn.dataset.goto;
      goTo(target === "next" ? active + 1 : target === "prev" ? active - 1 : parseInt(target, 10));
    });

    function onKey(e) {
      if (!inView || e.altKey || e.ctrlKey || e.metaKey) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "ArrowRight") { e.preventDefault(); goTo(active + 1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); goTo(active - 1); }
    }

    /* ---- only run video while the deck is on screen / tab is visible */
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        inView = entry.isIntersecting;
        if (inView) { load(active, true); playVideo(active); renderGate(); scheduleStamp(keys[active]); }
        else { stopVideo(active, true); clearTimeout(stampTimer); }
      });
    }, { threshold: 0.15 });
    io.observe(stage);

    function onVisibility() {
      if (doc.hidden) stopVideo(active, true);
      else if (inView) playVideo(active);
    }

    // Warm the first clip as soon as the deck is one screen away.
    const warm = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) { load(0, true); load(1, false); warm.disconnect(); }
    }, { rootMargin: "100% 0px" });
    warm.observe(section);

    const unsubscribe = sound.subscribe(applySound);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    doc.addEventListener("keydown", onKey);
    doc.addEventListener("visibilitychange", onVisibility);

    // Theme editor: clicking a block in the sidebar jumps to that slide.
    function onBlockSelect(e) {
      const idx = chapters.findIndex((c) => c === e.target || c.contains(e.target));
      if (idx >= 0) goTo(idx);
    }
    doc.addEventListener("shopify:block:select", onBlockSelect);

    // Initial state (handles reload with restored scroll position).
    measure();
    theme.set(chapters[0].dataset.accent, chapters[0].dataset.glow, chapters[0].dataset.chapter);
    const startTop = trackEl.getBoundingClientRect().top;
    const startIdx = clamp(Math.round(clamp(-startTop / stepPx, 0, count)), 0, count - 1);
    if (startIdx !== 0) activate(startIdx, 1, true);
    renderGate();
    update();

    return {
      goTo,
      goToKey,
      get active() { return active; },
      get keys() { return keys; },
      destroy() {
        unsubscribe();
        io.disconnect();
        warm.disconnect();
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
        window.removeEventListener("orientationchange", onResize);
        doc.removeEventListener("keydown", onKey);
        doc.removeEventListener("visibilitychange", onVisibility);
        doc.removeEventListener("shopify:block:select", onBlockSelect);
        videos.forEach((v) => { if (v) { v.pause(); } });
        clearTimeout(stampTimer);
        clearTimeout(leaveTimer);
      }
    };
  }

  function bootChapters() {
    if (chaptersApi) { chaptersApi.destroy(); chaptersApi = null; }
    const section = doc.querySelector("[data-chapters]");
    if (section) chaptersApi = initChapters(section);
  }

  /* ------------------------------------------------------------------ hero (drag the pack) */
  const flavours = [
    { key: "amritsar", city: "AMRITSAR", place: "AMRITSAR · PUNJAB", title: "Tandoori heat meets<br>Punjabi beat 🔥🥁", note: "One crunch and you’re doing the Bhangra! 🕺",
      bg: "radial-gradient(circle at 60% 45%, #b92c16 0 17%, #5d110b 42%, #150908 75%)", artPosition: "33.333% 50%", accent: "#ef351d", glow: "#ffb21c" },
    { key: "delhi", city: "DELHI", place: "DELHI · NCR", title: "RICH CREAMY<br>LEGENDARY", note: "Delhi’s OG flavor in every single crunch. 🤌🔥",
      bg: "radial-gradient(circle at 58% 44%, #f05c25 0 16%, #752014 40%, #12090b 76%)", artPosition: "0% 50%", accent: "#ef4c22", glow: "#30f2e7" },
    { key: "goa", city: "GOA", place: "GOA · KONKAN COAST", title: "THE ULTIMATE<br>COASTAL CATCH", note: "Sea Breeze, Shack Vibes and Unforgettable Coastal Crunch 🌊🌴",
      bg: "radial-gradient(circle at 58% 43%, #16a9a1 0 16%, #075e64 42%, #061b27 76%)", artPosition: "100% 50%", accent: "#08a59b", glow: "#ffdf2b" },
    { key: "chennai", city: "CHENNAI", place: "CHENNAI · TAMIL NADU", title: "LIGHTS CAMERA<br>CRUNCH", note: "One bite and you’re in the front row of South Indian flavor! 🎬🕺",
      bg: "radial-gradient(circle at 58% 43%, #a5c900 0 16%, #4f6910 40%, #160912 76%)", artPosition: "66.667% 50%", accent: "#a5c900", glow: "#ffcc21" }
  ];

  function initHero() {
    const hero = doc.querySelector(".hero");
    const deck = doc.querySelector(".packet-deck");
    const dots = doc.querySelector(".selector-dots");
    if (!hero || !deck || !dots) return null;

    const packets = [...deck.querySelectorAll(".packet")];
    const cityWord = hero.querySelector(".city-word");
    const counter = hero.querySelector(".counter");
    const place = hero.querySelector(".place");
    const title = hero.querySelector(".flavour-card h2");
    const note = hero.querySelector(".note");
    const enter = hero.querySelector(".enter-button");
    const art = (window.iCrunchAssets && window.iCrunchAssets.cityThemes) ? `url("${window.iCrunchAssets.cityThemes}")` : "none";
    let activeIndex = 0;
    let pointerStart = null;

    dots.innerHTML = "";
    flavours.forEach((f, i) => {
      const dot = doc.createElement("button");
      dot.type = "button";
      dot.setAttribute("aria-label", `Select ${f.city}`);
      dot.addEventListener("click", () => setFlavour(i));
      dots.appendChild(dot);
    });

    function setFlavour(index, silent) {
      activeIndex = (index + flavours.length) % flavours.length;
      const f = flavours[activeIndex];
      if (!silent) theme.set(f.accent, f.glow, f.key);
      hero.style.setProperty("--hero-bg", f.bg);
      hero.style.setProperty("--hero-art", art);
      hero.style.setProperty("--hero-art-position", f.artPosition);
      if (cityWord) cityWord.textContent = f.city;
      if (counter) counter.textContent = `${pad2(activeIndex + 1)} / ${pad2(flavours.length)}`;
      if (place) place.textContent = f.place;
      if (title) title.innerHTML = f.title;
      if (note) note.textContent = f.note;
      if (enter) enter.innerHTML = `Enter ${f.city[0] + f.city.slice(1).toLowerCase()} <span class="flight-icon" aria-hidden="true">✈︎</span>`;

      packets.forEach((packet, i) => {
        packet.className = "packet";
        packet.setAttribute("aria-selected", i === activeIndex ? "true" : "false");
        if (i === activeIndex) packet.classList.add("active");
        else if (i === (activeIndex - 1 + flavours.length) % flavours.length) packet.classList.add("prev");
        else if (i === (activeIndex + 1) % flavours.length) packet.classList.add("next");
      });
      [...dots.children].forEach((dot, i) => dot.classList.toggle("active", i === activeIndex));
    }

    const moveBy = (direction) => setFlavour(activeIndex + direction);
    const enterChapter = () => {
      const key = flavours[activeIndex].key;
      track("icrunch_enter_city", { city: key });
      if (chaptersApi) chaptersApi.goToKey(key);
      else { const s = doc.querySelector("#chapters") || doc.querySelector("#map"); if (s) s.scrollIntoView({ behavior: "smooth" }); }
    };

    deck.addEventListener("pointerdown", (e) => { pointerStart = e.clientX; deck.setPointerCapture(e.pointerId); });
    deck.addEventListener("pointerup", (e) => {
      if (pointerStart === null) return;
      const distance = e.clientX - pointerStart;
      if (Math.abs(distance) > 35) moveBy(distance < 0 ? 1 : -1);
      pointerStart = null;
    });
    deck.addEventListener("pointercancel", () => { pointerStart = null; });
    deck.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); moveBy(1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); moveBy(-1); }
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); enterChapter(); }
    });
    packets.forEach((packet, i) => packet.addEventListener("click", () => { if (i !== activeIndex) setFlavour(i); }));
    if (enter) enter.addEventListener("click", enterChapter);

    // Keep the hero pack in step with whatever chapter is on screen.
    doc.addEventListener("icrunch:chapter", (e) => {
      const i = flavours.findIndex((f) => f.key === e.detail.key);
      if (i >= 0 && i !== activeIndex) setFlavour(i, true);
    });

    setFlavour(0);
    return { setFlavour };
  }

  /* ------------------------------------------------------------------ map list → chapters */
  function initMap() {
    const buttons = [...doc.querySelectorAll("[data-map-key]")];
    if (!buttons.length) return;
    buttons.forEach((button) => button.addEventListener("click", () => {
      const key = button.dataset.mapKey;
      track("icrunch_map_pick", { city: key });
      if (chaptersApi) chaptersApi.goToKey(key);
      else { const hero = doc.querySelector(".hero"); if (hero) hero.scrollIntoView({ behavior: "smooth" }); }
    }));
    doc.addEventListener("icrunch:chapter", (e) => {
      buttons.forEach((b) => b.classList.toggle("is-current", b.dataset.mapKey === e.detail.key));
    });
  }

  /* ------------------------------------------------------------------ passport */
  function initPassport() {
    const el = doc.querySelector("[data-passport]");
    if (!el) return null;
    const stamps = [...el.querySelectorAll("[data-stamp]")];
    const keys = stamps.map((s) => s.dataset.stamp);
    const countEl = el.querySelector("[data-passport-count]");
    const card = el.querySelector("[data-passport-card]");
    const toggle = el.querySelector("[data-passport-toggle]");
    const shareBtn = el.querySelector("[data-passport-share]");
    const closeBtn = el.querySelector("[data-passport-close]");
    const have = new Set((store.get("icrunch:passport", []) || []).filter((k) => keys.includes(k)));
    let celebrated = store.get("icrunch:passport:done", false) === true;

    function render() {
      stamps.forEach((s) => s.classList.toggle("is-stamped", have.has(s.dataset.stamp)));
      if (countEl) countEl.textContent = have.size;
      el.classList.toggle("is-complete", have.size === keys.length);
      if (have.size > 0) el.classList.add("is-visible");
    }
    // On phones the pill only appears once the visitor reaches the chapters.
    const deck = doc.querySelector("[data-chapters]");
    if (deck) {
      const reveal = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) { el.classList.add("is-visible"); reveal.disconnect(); }
      }, { threshold: 0.1 });
      reveal.observe(deck);
    } else { el.classList.add("is-visible"); }
    function openCard() { if (!card) return; card.hidden = false; toggle.setAttribute("aria-expanded", "true"); }
    function closeCard() { if (!card) return; card.hidden = true; toggle.setAttribute("aria-expanded", "false"); }

    function confetti() {
      if (reduceMotion) return;
      const canvas = doc.createElement("canvas");
      canvas.className = "confetti-canvas";
      body.appendChild(canvas);
      const ctx = canvas.getContext("2d");
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const W = canvas.width = window.innerWidth * dpr;
      const H = canvas.height = window.innerHeight * dpr;
      const css = getComputedStyle(root);
      const colours = [css.getPropertyValue("--accent").trim() || "#ef351d", css.getPropertyValue("--glow").trim() || "#ffb21c", "#f4ebdb", "#08a59b", "#a5c900", "#30f2e7"];
      const bits = Array.from({ length: 150 }, () => ({
        x: W * 0.5 + (Math.random() - 0.5) * W * 0.35, y: H * 0.8,
        vx: (Math.random() - 0.5) * 22 * dpr, vy: -(11 + Math.random() * 17) * dpr, g: 0.42 * dpr,
        w: (6 + Math.random() * 8) * dpr, h: (4 + Math.random() * 6) * dpr,
        r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.35,
        c: colours[Math.floor(Math.random() * colours.length)]
      }));
      const start = performance.now();
      const frame = (t) => {
        const elapsed = t - start;
        ctx.clearRect(0, 0, W, H);
        ctx.globalAlpha = clamp(1 - (elapsed - 1500) / 800, 0, 1);
        bits.forEach((p) => {
          p.vy += p.g; p.x += p.vx; p.y += p.vy; p.vx *= 0.99; p.r += p.vr;
          ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r); ctx.fillStyle = p.c;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); ctx.restore();
        });
        if (elapsed < 2400) requestAnimationFrame(frame); else canvas.remove();
      };
      requestAnimationFrame(frame);
    }

    function stamp(key) {
      if (!keys.includes(key) || have.has(key)) return;
      have.add(key);
      store.set("icrunch:passport", [...have]);
      render();
      const s = stamps.find((x) => x.dataset.stamp === key);
      if (s) { s.classList.add("just-stamped"); setTimeout(() => s.classList.remove("just-stamped"), 800); }
      if (navigator.vibrate && !reduceMotion) { try { navigator.vibrate(25); } catch (e) { /* noop */ } }
      track("icrunch_stamp", { city: key, total: have.size });
      if (have.size === keys.length && !celebrated) {
        celebrated = true;
        store.set("icrunch:passport:done", true);
        openCard();
        confetti();
        track("icrunch_passport_complete");
      }
    }

    toggle.addEventListener("click", () => {
      if (card && !card.hidden) { closeCard(); return; }
      if (have.size === keys.length) { openCard(); return; }
      const next = keys.find((k) => !have.has(k));
      if (chaptersApi && next) chaptersApi.goToKey(next);
      else { const s = doc.querySelector("#chapters"); if (s) s.scrollIntoView({ behavior: "smooth" }); }
    });
    if (closeBtn) closeBtn.addEventListener("click", closeCard);
    if (shareBtn) shareBtn.addEventListener("click", async () => {
      const text = shareBtn.dataset.shareText || "I just crunched across India with iCrunch.";
      const url = location.origin + location.pathname;
      try {
        if (navigator.share) await navigator.share({ title: doc.title, text, url });
        else if (navigator.clipboard) {
          await navigator.clipboard.writeText(`${text} ${url}`);
          const original = shareBtn.innerHTML;
          shareBtn.textContent = "Copied — paste it anywhere";
          setTimeout(() => { shareBtn.innerHTML = original; }, 2200);
        }
        track("icrunch_passport_share");
      } catch (e) { /* user cancelled the share sheet */ }
    });
    doc.addEventListener("keydown", (e) => { if (e.key === "Escape" && card && !card.hidden) closeCard(); });

    render();
    return { stamp };
  }

  /* ------------------------------------------------------------------ analytics hooks */
  function initAnalytics() {
    doc.addEventListener("click", (e) => {
      const el = e.target.closest("[data-track]");
      if (el) track(`icrunch_${el.dataset.track}`, { href: el.getAttribute("href") || "" });
    });
    const form = doc.getElementById("iCrunchContact");
    if (form) form.addEventListener("submit", () => {
      const reason = form.querySelector('[name="contact[reason]"]');
      track("icrunch_contact_submit", { reason: reason ? reason.value : "" });
    });
    const success = doc.querySelector(".form-success");
    if (success) { success.focus({ preventScroll: false }); track("icrunch_contact_success"); }
  }

  /* ------------------------------------------------------------------ boot */
  function boot() {
    bootChapters();
    initHero();
    initMap();
    passportApi = initPassport();
    initAnalytics();
  }

  if (doc.readyState === "loading") doc.addEventListener("DOMContentLoaded", boot);
  else boot();

  // Theme editor re-renders sections in place; rebuild the deck when that happens.
  doc.addEventListener("shopify:section:load", (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector("[data-chapters]")) bootChapters();
    if (e.target && e.target.querySelector && e.target.querySelector(".hero")) initHero();
  });
  doc.addEventListener("shopify:section:unload", (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector("[data-chapters]") && chaptersApi) { chaptersApi.destroy(); chaptersApi = null; }
  });

  window.iCrunch = { get chapters() { return chaptersApi; }, sound, track };
})();
